import { Calendar, Views, luxonLocalizer, EventProps, stringOrDate, SlotInfo } from 'react-big-calendar';
import React, { useEffect } from 'react';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useState } from 'react';
import { DateTime, Interval } from 'luxon';
import { faUserHelmetSafety } from '@fortawesome/pro-solid-svg-icons';
import { useEmployees, useMads, useProvisionalCalendarLines, useWorkPlaces } from '../hooks/useSwr';
import './Schedule.scss';

import {
    capitalize,
    filter,
    find,
    get,
    isDate,
    map,
    startCase,
    uniqBy,
    size,
    toNumber,
    sortBy,
    omit,
    flow,
} from 'lodash/fp';
import './Schedule.scss';
import {
    unlink,
    saveOrCreateWithoutRead,
    standardizedOdooProvisionalWithRecurrence,
    unstandardizedOdooProvisionalWithRecurrence,
} from '../utils/odoo';
import { ProvisionalWithRecurrence, ProvisionalCalendarToSaved } from '../types/models';
import {
    IonButton,
    IonButtons,
    IonCheckbox,
    IonCol,
    IonContent,
    IonGrid,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonPage,
    IonRow,
    IonSelect,
    IonSelectOption,
    IonTextarea,
    IonToolbar,
    useIonAlert,
    useIonModal,
    useIonToast,
} from '@ionic/react';
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces';
import { Many2OneSelector } from '../components/Many2OneSelector';
import { faClock, faHandshake, faPencil, faArrowsSpin } from '@fortawesome/pro-regular-svg-icons';
import { arrowBack, arrowForward, checkmark, closeOutline, today, warningOutline } from 'ionicons/icons';
import { RoundedSquareIcon } from '../components/Icon';
import { defaultToastOptions } from '../utils/Global';
import { NavigateAction } from 'react-big-calendar';
// @ts-ignore
import TimeGrid from 'react-big-calendar/lib/TimeGrid';
import { dateFromOdoo } from '../utils/date';

class CalendarResource {
    id: number;
    title: string;

    constructor(id: number, title: string) {
        this.id = id;
        this.title = title;
    }
}

const DnDCalendar = withDragAndDrop<ProvisionalWithRecurrence, CalendarResource>(Calendar);

interface MyWeekProps {
    date: Date;
}

export class MyWeek extends React.Component<MyWeekProps> {
    static title = (date: Date) => date.toLocaleDateString();

    static navigate = (date: Date, action: NavigateAction) => {
        switch (action) {
            case 'PREV':
                return DateTime.fromJSDate(date).minus({ day: 3 }).toJSDate();
            case 'NEXT':
                return DateTime.fromJSDate(date).plus({ day: 3 }).toJSDate();
            default:
                return date;
        }
    };

    static range = (date: Date) => {
        let start = DateTime.fromJSDate(date).startOf('day');
        let end = DateTime.fromJSDate(date).plus({ day: 2 }).endOf('day');

        let current = start;
        let range = [];
        while (current <= end) {
            range.push(current.toJSDate());
            current = current.plus({ day: 1 });
        }

        return range;
    };

    render() {
        let { date, ...props } = this.props;
        let range = MyWeek.range(date);

        return (
            <TimeGrid
                date={date}
                localizer={luxonLocalizer(DateTime)}
                scrollToTime={DateTime.now().startOf('day').toJSDate()}
                min={DateTime.now().startOf('day').toJSDate()}
                max={DateTime.now().endOf('day').toJSDate()}
                range={range}
                //TODO: use appconfig rouding ? pertinent ?
                eventOffset={15}
                {...props}
            />
        );
    }
}

const CustomEvent: React.FC<{
    event: EventProps<ProvisionalWithRecurrence>;
    employeeName?: string;
    madName?: string;
    workplaceName?: string;
}> = ({ event, employeeName = '', madName = '', workplaceName = '' }) => {
    return (
        <div className="flex flex-col text-[12px] h-90">
            <div className="mt-1 mb-2">{employeeName}</div>
            <div className="mb-2">{madName}</div>
            <div className="mb-2">{workplaceName}</div>
            <div className="calendar-duration">
                <div className="text-[12px]">
                    {event.event.date_end.diff(event.event.date_start).toFormat('hh:mm:ss')}
                </div>
            </div>
        </div>
    );
};
const ScheduleModal: React.FC<{
    item: ProvisionalWithRecurrence;
    onDismiss: (data?: ProvisionalWithRecurrence, role?: string) => void;
}> = ({ item, onDismiss }) => {
    const mads = filter((e) => get('employee_id[0]', e) === item.employee_id, useMads().data);
    const { data: workPlaces } = useWorkPlaces();
    const [event, setEvent] = useState<ProvisionalWithRecurrence>(item);
    const isOldEvent = item.id > 0 && item.date_start < DateTime.now();
    const minDate = DateTime.now();
    const maxDate = DateTime.local(minDate.year + 30) as DateTime<true>;

    function setTimeToDateTime(date: DateTime, time: string): DateTime {
        const temp = DateTime.fromISO(time);
        return date.set({ hour: temp.hour, minute: temp.minute });
    }

    function setDateToDateTime(date: DateTime, newDate: string): DateTime {
        const temp = DateTime.fromISO(newDate);
        return date.set({ year: temp.year, month: temp.month, day: temp.day });
    }

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="primary" onClick={() => onDismiss(undefined, 'cancel')}>
                            <IonIcon icon={arrowBack} />
                        </IonButton>
                    </IonButtons>
                    {!isOldEvent && (
                        <IonButtons slot="end">
                            {event.id > 0 && (
                                <IonButton
                                    color="danger"
                                    onClick={() => {
                                        onDismiss(event, 'delete');
                                    }}
                                >
                                    <IonIcon icon={closeOutline} />
                                    Supprimer
                                </IonButton>
                            )}
                            <IonButton
                                disabled={
                                    !event.mad_id ||
                                    event.date_start <= minDate ||
                                    event.date_end <= event.date_start ||
                                    DateTime.max(event.date_end, event.date_end_recurrent) >= maxDate
                                }
                                color="success"
                                onClick={() => {
                                    onDismiss(event, 'confirm');
                                }}
                            >
                                <IonIcon icon={checkmark} />
                                Valider
                            </IonButton>
                        </IonButtons>
                    )}
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding schedule-form space-y-1">
                <div className="shadow-sm">
                    <Many2OneSelector
                        items={mads}
                        value={event.mad_id}
                        label="Client"
                        lines="none"
                        icon={faHandshake}
                        fieldToDisplay="display_name"
                        selectOptions={{
                            placeholder: 'Sélectionner un client',
                            interfaceOptions: {
                                header: 'Client',
                                subHeader: 'Choisissez un client',
                                translucent: true,
                            },
                        }}
                        onChange={(value) => {
                            if (value) {
                                setEvent({ ...event, mad_id: value.id, workplace_id: 0 });
                            }
                        }}
                        disabled={isOldEvent}
                    />
                </div>
                <div className="shadow-sm">
                    <Many2OneSelector
                        items={filter((e) => {
                            return e.mad_ids.some((id) => id === event.mad_id);
                        }, workPlaces)}
                        value={event.workplace_id}
                        label="Chantier"
                        lines="none"
                        icon={faUserHelmetSafety}
                        selectOptions={{
                            placeholder: 'Sélectionner un chantier',
                            interfaceOptions: {
                                header: 'Chantier',
                                subHeader: 'Choisissez un chantier',
                                translucent: true,
                            },
                        }}
                        onChange={(value) => {
                            if (value) {
                                setEvent({ ...event, workplace_id: value.id });
                            }
                        }}
                        disabled={isOldEvent}
                    />
                </div>
                <div className="bg-white schedule-datetime shadow-sm">
                    <IonRow className="pl-3 h-full py-1">
                        <IonCol className="flex h-full">
                            <RoundedSquareIcon icon={faClock} />
                            <span className="pl-1 mt-1">Début</span>
                        </IonCol>
                        <div id="dateTimeStart">
                            <input
                                type="date"
                                name="startDate"
                                onChange={(e) => {
                                    setEvent({
                                        ...event,
                                        date_start: setDateToDateTime(event.date_start, e.target.value),
                                    });
                                }}
                                value={event.date_start.toFormat('yyyy-MM-dd')}
                                className="h-full"
                                disabled={isOldEvent}
                                min={minDate.toISODate()}
                                max={maxDate.toISODate()}
                            />
                            <input
                                type="time"
                                onChange={(e) => {
                                    setEvent({
                                        ...event,
                                        date_start: setTimeToDateTime(event.date_start, e.target.value),
                                    });
                                }}
                                value={event.date_start.toFormat('HH:mm')}
                                name="startTime"
                                className="h-full"
                                disabled={isOldEvent}
                            />
                        </div>
                    </IonRow>
                </div>
                <div className="bg-white schedule-datetime shadow-sm">
                    <IonRow className="pl-3 h-full py-1">
                        <IonCol className="flex h-full">
                            <RoundedSquareIcon icon={faClock} />
                            <span className="pl-1 mt-1">Fin</span>
                        </IonCol>
                        <div id="dateTimeEnd">
                            <input
                                type="date"
                                name="endDate"
                                onChange={(e) => {
                                    setEvent({
                                        ...event,
                                        date_end: setDateToDateTime(event.date_end, e.target.value),
                                    });
                                }}
                                value={event.date_end.toFormat('yyyy-MM-dd')}
                                className="h-full"
                                disabled={isOldEvent}
                                min={minDate.toISODate()}
                                max={maxDate.toISODate()}
                            />
                            <input
                                type="time"
                                onChange={(e) => {
                                    setEvent({
                                        ...event,
                                        date_end: setTimeToDateTime(event.date_end, e.target.value),
                                    });
                                }}
                                value={event.date_end.toFormat('HH:mm')}
                                name="endTime"
                                className="h-full"
                                disabled={isOldEvent}
                            />
                        </div>
                    </IonRow>
                </div>
                <IonItem className="bg-white shadow-sm" lines="none">
                    <IonGrid className="pl-0">
                        <IonRow>
                            <RoundedSquareIcon icon={faPencil} color={'bg-night'} />
                            <IonLabel className="pl-1 leading-7">Note</IonLabel>
                        </IonRow>
                        <IonRow>
                            <div className="w-full">
                                <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                                <IonTextarea
                                    value={event.note || ''}
                                    placeholder="Tapez quelque chose ici"
                                    aria-label="Note"
                                    onInput={(e: any) => {
                                        setEvent({ ...event, note: e.target.value });
                                    }}
                                    disabled={isOldEvent}
                                    autoGrow={true}
                                />
                            </div>
                        </IonRow>
                    </IonGrid>
                </IonItem>
                {(event.id <= 0 && (
                    <div className="bg-white schedule-datetime shadow-sm">
                        <IonRow className="pl-[1.15rem] h-full py-1">
                            <IonCol className="flex h-full pt-2 pb-2">
                                <IonCheckbox
                                    className="w-6 h-6 rounded-md"
                                    checked={event.is_recurrent}
                                    onIonChange={(e: any) => {
                                        setEvent({ ...event, is_recurrent: e.detail.checked });
                                    }}
                                    labelPlacement="end"
                                    justify="space-between"
                                    disabled={isOldEvent}
                                >
                                    Récurrent
                                </IonCheckbox>
                            </IonCol>
                            {event.is_recurrent && (
                                <>
                                    <div className="mr-2">
                                        <IonSelect
                                            placeholder="Occurence"
                                            value={event.recurrence_type}
                                            className="h-full"
                                            onIonChange={(e: any) => {
                                                setEvent({ ...event, recurrence_type: e.detail.value });
                                            }}
                                            disabled={isOldEvent}
                                        >
                                            <IonSelectOption value="day">Jour</IonSelectOption>
                                            <IonSelectOption value="week">Semaine</IonSelectOption>
                                        </IonSelect>
                                    </div>
                                    <div>
                                        Fin :
                                        <input
                                            type="date"
                                            name="endRecurrence"
                                            onChange={(e) => {
                                                setEvent({
                                                    ...event,
                                                    date_end_recurrent: setDateToDateTime(
                                                        event.date_end_recurrent,
                                                        e.target.value
                                                    ),
                                                });
                                            }}
                                            value={event.date_end_recurrent.toFormat('yyyy-MM-dd')}
                                            className="h-10"
                                            disabled={isOldEvent}
                                            min={minDate.toISODate()}
                                            max={maxDate.toISODate()}
                                        />
                                    </div>
                                </>
                            )}
                        </IonRow>
                    </div>
                )) ||
                    (event.is_recurrent && (
                        <>
                            <div
                                className="bg-white p-3 schedule-datetime content-center shadow-sm"
                                style={{ textAlign: 'center' }}
                            >
                                <IonIcon size="large" color="warning" icon={warningOutline} />
                                <p>Cet événement est récurrent</p>
                            </div>
                            <div className="bg-white schedule-datetime shadow-sm">
                                <IonRow className="pl-3 h-full py-1">
                                    <IonCol className="flex h-full">
                                        <RoundedSquareIcon icon={faArrowsSpin} />
                                        <span className="pl-1 mt-1">Fin de récurrence</span>
                                    </IonCol>
                                    <input
                                        type="date"
                                        name="endRecurrence"
                                        onChange={(e) => {
                                            setEvent({
                                                ...event,
                                                date_end_recurrent: setDateToDateTime(
                                                    event.date_end_recurrent,
                                                    e.target.value
                                                ),
                                            });
                                        }}
                                        value={event.date_end_recurrent.toFormat('yyyy-MM-dd')}
                                        className="h-10"
                                        disabled={isOldEvent}
                                    />
                                </IonRow>
                            </div>
                        </>
                    ))}
            </IonContent>
        </IonPage>
    );
};

const Schedule: React.FC = () => {
    let { data: OdooProvisionalCalendarLines, mutate: mutateProvisionalCalendarLines } = useProvisionalCalendarLines();
    const [presentToast] = useIonToast();

    const events = map((event): ProvisionalWithRecurrence => {
        const standardizedEvent = standardizedOdooProvisionalWithRecurrence(event);
        const eventWithSameUuid = sortBy(
            'date_end',
            filter({ link_uuid: standardizedEvent.link_uuid }, OdooProvisionalCalendarLines)
        );
        return {
            ...standardizedEvent,
            is_recurrent: size(eventWithSameUuid) > 1,
            date_end_recurrent: DateTime.max(...map((event) => dateFromOdoo(event.date_end), eventWithSameUuid)),
            recurrence_type:
                (size(eventWithSameUuid) > 1 &&
                    (Interval.fromDateTimes(standardizedEvent.date_end, dateFromOdoo(eventWithSameUuid[1].date_end))
                        .toDuration()
                        .as('days') === 1
                        ? 'day'
                        : 'week')) ||
                null,
        };
    }, OdooProvisionalCalendarLines);

    const [actual, setActual] = useState<ProvisionalWithRecurrence | null>(null);
    const [presentAlert] = useIonAlert();
    const [present, dismiss] = useIonModal(ScheduleModal, {
        item: actual,
        onDismiss: (data: ProvisionalWithRecurrence, role: string) => dismiss(data, role),
        is_recurrent: actual?.link_uuid && size(filter({ link_uuid: actual.link_uuid }, events)) > 1,
    });
    const { data: employees } = useEmployees([
        ['service', '=', 'mad'],
        ['mad_ids', '!=', false],
    ]);
    const { data: mads } = useMads();
    const { data: workPlaces } = useWorkPlaces();
    const resources = uniqBy(
        'id',
        map(
            (d) => ({
                id: d.id,
                title: d.name,
            }),
            employees
        )
    );

    const modifyEvent = async (event: ProvisionalWithRecurrence, operation: 'update' | 'delete' = 'update') => {
        const handler = async () => {
            let context = {};
            if (event.is_recurrent) {
                context = {
                    date_end_recurrent: event.date_end_recurrent
                        ? event.date_end_recurrent.toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
                        : false,
                    ...(event.id <= 0
                        ? { recurrence_type: event.recurrence_type }
                        : { apply_to_recurrence: !!event.is_application_global }),
                };
            }
            operation === 'update'
                ? await saveOrCreateWithoutRead<ProvisionalCalendarToSaved>(
                      'hr.madper.app.pointing.provisional.calendar',
                      unstandardizedOdooProvisionalWithRecurrence(
                          flow(
                              omit(['is_recurrent', 'date_end_recurrent', 'recurrence_type', 'is_application_global'])
                          )(event)
                      ),
                      context
                  )
                : await unlink('hr.madper.app.pointing.provisional.calendar', event.id, context);
            mutateProvisionalCalendarLines(OdooProvisionalCalendarLines);
        };
        if (event.date_start < DateTime.now()) {
            notifyUnpossibilityToChangeEvent();
        } else if (event.id && event.is_recurrent) {
            await presentAlert({
                header: 'Alerte récurrence',
                message: `Vous demandez à ${
                    operation === 'update' ? 'modifier' : 'supprimer'
                } un événement récurrent. Souhaitez-vous ${
                    operation === 'update' ? 'appliquer cette modification aux' : 'également supprimer les'
                } événements associés ?`,
                buttons: [
                    {
                        text: 'Oui',
                        handler: () => {
                            event.is_application_global = true;
                            handler();
                        },
                    },
                    {
                        text: 'Non, uniquement celui-ci',
                        handler: handler,
                    },
                    {
                        text: 'Annuler',
                        handler: () => {
                            return;
                        },
                    },
                ],
            });
        } else if (operation === 'delete') {
            await presentAlert({
                header: 'Alerte suppression',
                message: `Êtes-vous sûr de vouloir supprimer cet événement ?`,
                buttons: [
                    {
                        text: 'Oui',
                        handler: () => {
                            handler();
                        },
                    },
                    {
                        text: 'Annuler',
                        handler: () => {
                            return;
                        },
                    },
                ],
            });
        } else {
            handler();
        }
    };

    const notifyUnpossibilityToChangeEvent = () => {
        presentToast({
            ...defaultToastOptions,
            message: `Vous ne pouvez pas intervenir sur un événement passé`,
            duration: 3000,
            color: 'warning',
        });
    };

    const applyEventChanges = (data: {
        event: ProvisionalWithRecurrence;
        start: stringOrDate;
        end: stringOrDate;
        resourceId?: any;
    }): ProvisionalWithRecurrence => {
        let tempData = { ...data.event };
        tempData.date_start = isDate(data.start) ? DateTime.fromJSDate(data.start) : DateTime.fromISO(data.start);
        tempData.date_end = isDate(data.end) ? DateTime.fromJSDate(data.end) : DateTime.fromISO(data.end);
        tempData.employee_id = events.find((e) => e.employee_id === data.resourceId)?.employee_id || -1;
        tempData.is_recurrent = filter({ link_uuid: tempData.link_uuid }, events).length > 1;
        return tempData;
    };

    const onEventDrop = (data: {
        event: ProvisionalWithRecurrence;
        start: stringOrDate;
        end: stringOrDate;
        resourceId?: any;
    }) => {
        if (data.event.date_start < DateTime.now()) {
            notifyUnpossibilityToChangeEvent();
        } else if (data.resourceId === data.event.employee_id) {
            modifyEvent(applyEventChanges(data));
        } else {
            presentToast({
                ...defaultToastOptions,
                message: `Vous ne pouvez pas transférer d'événement d'un employé à un autre`,
                duration: 3000,
                color: 'warning',
            });
        }
    };

    const onEventResize = (data: {
        event: ProvisionalWithRecurrence;
        start: stringOrDate;
        end: stringOrDate;
        resourceId?: any;
    }) => {
        if (data.event.date_start < DateTime.now()) {
            notifyUnpossibilityToChangeEvent();
        } else {
            modifyEvent(applyEventChanges(data));
        }
    };

    const onSelectSlot = (data: SlotInfo) => {
        if (DateTime.fromJSDate(data.start) < DateTime.now()) {
            presentToast({
                ...defaultToastOptions,
                message: `Vous ne pouvez pas créer d'événement passé`,
                duration: 3000,
                color: 'warning',
            });
            return;
        }
        setActual({
            id: 0,
            date_end_recurrent: DateTime.fromJSDate(data.end),
            date_end: DateTime.fromJSDate(data.end),
            date_start: DateTime.fromJSDate(data.start),
            employee_id: find({ id: toNumber(data.resourceId) }, employees)?.id || -1,
            is_application_global: false,
            is_recurrent: false,
            link_uuid: '',
            mad_id: 0,
            note: '',
            recurrence_type: 'day',
            workplace_id: 0,
        });
        present({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                if (ev.detail.role === 'confirm') modifyEvent(ev.detail.data);
            },
        });
    };

    const onSelectEvent = (event: ProvisionalWithRecurrence) => {
        setActual(event);
        present({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                if (ev.detail.role === 'confirm') modifyEvent(ev.detail.data);
                if (ev.detail.role === 'delete') modifyEvent(ev.detail.data, 'delete');
            },
        });
    };

    const stringToColour = (str: string) => {
        var hash = 0;
        for (var i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        var colour = '#';
        for (var j = 0; j < 3; j++) {
            var value = (hash >> (j * 8)) & 0xff;
            colour += ('00' + value.toString(16)).slice(-2);
        }
        return colour;
    };
    return (
        <div className="calendar-container">
            <DnDCalendar
                localizer={luxonLocalizer(DateTime)}
                defaultDate={new Date()}
                defaultView={Views.DAY}
                views={{ week: MyWeek, day: true, agenda: true }}
                events={events}
                onEventDrop={onEventDrop}
                onEventResize={onEventResize}
                onSelectSlot={onSelectSlot}
                onSelectEvent={onSelectEvent}
                toolbar
                popup
                popupOffset={20}
                selectable
                resizable
                eventPropGetter={(event: ProvisionalWithRecurrence) => ({
                    style: {
                        borderColor: stringToColour(event.employee_id.toString(6)),
                        opacity: event.date_end < DateTime.now() ? 0.5 : 1,
                        backgroundColor: event.date_end < DateTime.now() ? '#F6F6F6' : '#F1F1F1',
                        boxShadow: event.date_end < DateTime.now() ? '0.5px 0.5px 2px black' : '0px 0px 0.8px black',
                    },
                })}
                titleAccessor={(event: ProvisionalWithRecurrence) =>
                    `${find({ id: event.mad_id }, mads)?.name}\n${find({ id: event.workplace_id }, workPlaces)?.name}`
                }
                tooltipAccessor={(event: ProvisionalWithRecurrence) => event.note}
                formats={{
                    dayFormat: (date) => capitalize(DateTime.fromJSDate(date).toFormat('cccc dd')),
                    agendaDateFormat: (date) =>
                        startCase(
                            DateTime.fromJSDate(date).toLocaleString({
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                            })
                        ),
                }}
                startAccessor={(event: ProvisionalWithRecurrence) => event.date_start.toJSDate()}
                endAccessor={(event: ProvisionalWithRecurrence) =>
                    event.date_end.toJSDate() || event.date_start.toJSDate()
                }
                scrollToTime={DateTime.now().startOf('day').toJSDate()}
                messages={{
                    date: 'Date',
                    time: 'Temps',
                    event: 'Événement',
                    allDay: 'Jour entier',
                    week: 'Semaine',
                    work_week: 'Travail semaine',
                    day: 'Jour',
                    month: 'Mois',
                    previous: 'Précédent',
                    next: 'Suivant',
                    yesterday: 'Hier',
                    tomorrow: 'Demain',
                    today: "Aujourd'hui",
                    agenda: 'Agenda',
                    noEventsInRange: "Il n'y a pas d'événements dans cette gamme.",
                }}
                components={{
                    event: (event: EventProps<ProvisionalWithRecurrence>) => (
                        <CustomEvent
                            event={event}
                            employeeName={find({ id: event.event.employee_id }, employees)?.name}
                            madName={find({ id: event.event.mad_id }, mads)?.name}
                            workplaceName={find({ id: event.event.workplace_id }, workPlaces)?.name}
                        />
                    ),

                    toolbar: (toolbar: any) => <ToolbarCustom toolbar={toolbar} />,
                }}
                className={'my-calendar'}
                elementProps={{ id: 'myCalendar' }}
                resources={resources}
                resourceAccessor="employee_id"
                resourceIdAccessor="id"
                resourceTitleAccessor="title"
                showMultiDayTimes
                style={{ height: '90vh' }}
            />
        </div>
    );
};

const ToolbarCustom: React.FC<{
    toolbar: any;
}> = ({ toolbar }) => {
    const toolbarDate = DateTime.fromJSDate(toolbar.date);
    const [date, setDate] = useState<DateTime>(DateTime.fromJSDate(toolbar.date));

    useEffect(() => {
        setDate(DateTime.fromJSDate(toolbar.date));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toolbar.date]);

    return (
        <div className="rbc-toolbar flex justify-between items-center pt-3 px-4">
            <div className="rbc-btn-group flex-1 ml-10">
                <button
                    type="button"
                    className="rbc-btn"
                    onClick={() => {
                        if (toolbar.view === 'agenda') {
                            toolbar.onNavigate('prev', toolbarDate.minus({ month: 1 }).startOf('month').toJSDate());
                        } else {
                            toolbar.onNavigate('PREV', toolbarDate.toJSDate());
                        }
                    }}
                >
                    <IonIcon icon={arrowBack} />
                </button>
                <button
                    type="button"
                    className="rbc-btn"
                    onClick={() => {
                        toolbar.onNavigate('current', DateTime.now().toJSDate());
                    }}
                >
                    <IonIcon icon={today} />
                </button>
                <button
                    type="button"
                    className="rbc-btn"
                    onClick={() => {
                        if (toolbar.view === 'agenda') {
                            toolbar.onNavigate('next', toolbarDate.plus({ month: 1 }).startOf('month').toJSDate());
                        } else {
                            toolbar.onNavigate('NEXT', toolbarDate.toJSDate());
                        }
                    }}
                >
                    <IonIcon icon={arrowForward} />
                </button>
            </div>
            <div className="rbc-toolbar-label flex-1 text-center font-bold text-lg">
                {(toolbar.view === 'day' && (
                    <>
                        {toolbarDate.toFormat('cccc').charAt(0).toUpperCase() + toolbarDate.toFormat('cccc ').slice(1)}
                        <input
                            type="date"
                            className="schedule-date rounded-lg w-36 drop-shadow"
                            value={date.isValid ? (date as DateTime<true>).toISODate() : Date.now()}
                            onChange={(e) => {
                                const newDate = DateTime.fromISO(e.target.value);
                                if (!newDate.invalidReason) {
                                    setDate(newDate);
                                    toolbar.onNavigate('CURRENT', DateTime.fromISO(e.target.value).toJSDate());
                                }
                            }}
                        />
                    </>
                )) ||
                    (toolbar.view === 'week' && (
                        <>
                            <input
                                type="date"
                                className="schedule-date rounded-lg w-36 drop-shadow"
                                value={date.isValid ? (date as DateTime<true>).toISODate() : Date.now()}
                                onChange={(e) => {
                                    const newDate = DateTime.fromISO(e.target.value);
                                    if (!newDate.invalidReason) {
                                        setDate(newDate);
                                        toolbar.onNavigate('CURRENT', DateTime.fromISO(e.target.value).toJSDate());
                                    }
                                }}
                            />
                            {' - ' + (date.invalidReason ? Date.now() : date.plus({ day: 2 }).toFormat('dd/MM/yyyy'))}
                        </>
                    )) ||
                    (toolbar.view === 'agenda' && (
                        <input
                            type="month"
                            className="schedule-date rounded-lg drop-shadow"
                            value={date.invalidReason ? Date.now() : date.toFormat('yyyy-MM')}
                            onChange={(e) => {
                                const newDate = DateTime.fromISO(e.target.value);
                                if (!newDate.invalidReason) {
                                    setDate(newDate);
                                    toolbar.onNavigate('CURRENT', DateTime.fromISO(e.target.value).toJSDate());
                                }
                            }}
                        />
                    ))}
            </div>
            <div className="rbc-btn-group flex-1 flex justify-end">
                <button type="button" className="rbc-btn" onClick={() => toolbar.onView('day')}>
                    Jour
                </button>
                <button
                    type="button"
                    className="rbc-btn"
                    onClick={() => {
                        toolbar.onView('week');
                    }}
                >
                    3 jours
                </button>
                <button
                    type="button"
                    className="rbc-btn"
                    onClick={() => {
                        toolbar.onView('agenda');
                    }}
                >
                    Agenda
                </button>
            </div>
        </div>
    );
};

export default Schedule;
